import { TextInput, ActionIcon, Tooltip, Loader } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconSearch, IconX } from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { useStatusStore } from "@/utils/stores/useStatusStore";
import "./ClientListSearch.scss";

const ClientListSearch = () => {
  const { searchQuery, setSearchQuery } = useStatusStore();
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [debouncedSearchQuery] = useDebouncedValue(localSearchQuery, 300);

  // Sync local search query with store search query
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  // Update store when debounced value changes
  useEffect(() => {
    if (debouncedSearchQuery !== searchQuery) {
      setSearchQuery(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, searchQuery, setSearchQuery]);

  const handleClearSearch = () => {
    // Only clear the local search query - let the debounced value
    // propagate to the store naturally to avoid race conditions
    setLocalSearchQuery("");
  };

  const handleSearchKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      setSearchQuery(localSearchQuery);
    }
  };

  // Show loader while debouncing (when local differs from debounced)
  const isDebouncing = localSearchQuery !== debouncedSearchQuery;

  return (
    <div className="client-list-search-container">
      <TextInput
        placeholder="Search for a client"
        leftSection={<IconSearch size={18} style={{ color: "#9CA3AF" }} />}
        rightSection={
          isDebouncing ? (
            <Loader size={16} />
          ) : (
            localSearchQuery.trim() && (
              <Tooltip label="Clear search">
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClearSearch();
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  aria-label="Clear search"
                >
                  <IconX size={16} />
                </ActionIcon>
              </Tooltip>
            )
          )
        }
        value={localSearchQuery}
        onChange={(event) => setLocalSearchQuery(event.currentTarget.value)}
        onKeyDown={handleSearchKeyPress}
        size="md"
        data-testid="client-list-search"
      />
    </div>
  );
};

export default ClientListSearch;
